import React, { useState, useEffect, useContext, useRef } from "react";
import { makeStyles } from "@material-ui/core/styles";
import {
    Divider,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    Button,
} from "@material-ui/core";
import { DataGrid } from "@material-ui/data-grid";
import AuthContext from "../../context";
import { get, post, put, del } from "../../utils/api-services";
import ViewTitle from "../../common/view-title";
import Crud from "../../common/crud";
import DynamicForm from "../components/DynamicForm";
import catalogConfig from "../utils/catalogConfig";
import { Link } from "react-router-dom";
import { Tooltip, IconButton, Icon } from "@material-ui/core";
import Trash from "../../common/trash";

const useStyles = makeStyles(() => ({
    root: { display: "flex", flexDirection: "column", flex: 1 },
    divider: { marginBottom: 10 },
    formControl: { width: "100%", marginBottom: 10 },
    tableContainer: { marginTop: 20 },
    confirmDialog: {
        maxWidth: "100%",
        borderRadius: "8px",
        padding: "20px",
        backgroundColor: "#FFF",
    },
    confirmDialogActions: {
        justifyContent: "right",
        padding: "16px",
        backgroundColor: "#F5F5F5",
    },
    deleteButton: {
        backgroundColor: "#dc3545 !important",
        "&:hover": {
            backgroundColor: "#c82333 !important",
        },
        color: "white",
    },
}));

const Catalogs = ({ catalog }) => {
    const classes = useStyles();
    const { showToast, showLoader } = useContext(AuthContext);
    const sede = JSON.parse(localStorage.getItem("@sede") || "{}");
    const isOregonSede = sede?.name?.toLowerCase?.().includes("oregon") || false;
    const isFloridaSede =
        sede?.name?.toLowerCase?.().includes("florida") ||
        sede?.name?.toLowerCase?.().includes("deland") ||
        sede?.name?.toLowerCase?.().includes("miami") ||
        false;
    const floridaOnlyProductFields = ["EPA", "ingredients"];
    const [selectedCatalog, setSelectedCatalog] = useState("");
    const [data, setData] = useState([]);
    const [formData, setFormData] = useState({});
    const [selectedRow, setSelectedRow] = useState(null);
    const currentConfig = catalogConfig[selectedCatalog] || { fields: [] };
    const [selectedRows, setSelectedRows] = useState([]);
    const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
    const [pendingDeleteIds, setPendingDeleteIds] = useState([]);
    const [isCreating, setIsCreating] = useState(false);
    const [farmsById, setFarmsById] = useState({});
    const [acresRefreshKey, setAcresRefreshKey] = useState(0);
    const trashRef = useRef(null);
    const isMounted = useRef(true);
    const allowUpdate = selectedCatalog !== "productTypes";
    const trashTable = currentConfig?.trashTable || currentConfig?.tableName || selectedCatalog;
    const trashId = currentConfig?.idKey || "id";

    const formConfig = {
      ...currentConfig,
      fields: currentConfig.fields
        .filter(f => f.name !== 'code')
        .filter(f => !(isOregonSede && selectedCatalog === "sections" && f.name === "idfinca_area"))
        .filter(
            (f) =>
                !(
                    selectedCatalog === "products" &&
                    !isFloridaSede &&
                    floridaOnlyProductFields.includes(f.name)
                )
        )
        .map(f => ({
            ...f,
            showAvailableAcres:
                isOregonSede && selectedCatalog === "sections" && f.name === "idfinca_farm"
                    ? true : f.showAvailableAcres,
            disabled: !isCreating && f.isEditable === false
        }))
        .filter(f => !(!isCreating && f.name === 'idfinca_product_type'))
    };

    useEffect(() => {
        if (selectedCatalog) fetchData();
    }, [selectedCatalog]);

    useEffect(() => {
        isMounted.current = true;
        return () => {isMounted.current = false;};
    }, []);

    useEffect(() => {
        setFormData({});
    }, [selectedCatalog]);

    useEffect(() => {
        if (catalog) {
            setSelectedCatalog(catalog);
        }
    }, [catalog]);

    useEffect(() => {
        let active = true;

        const loadFarms = async () => {
            if (selectedCatalog !== "sections") {
                if (active) {setFarmsById({});}
                return;
            }

            try {
                const response = await get("/fincas/farms");
                if (response.status === 200 && Array.isArray(response.data) && active) {
                    const map = response.data.reduce((acc, farm) => {
                        if (farm?.idfinca_farm != null) {
                            acc[String(farm.idfinca_farm)] = farm?.name || "";
                        }
                        return acc;
                    }, {});
                    setFarmsById(map);
                }
            } catch (error) {
                if (active) {setFarmsById({});}
            }
        };

        loadFarms();
        return () => {
            active = false;
        };
    }, [selectedCatalog]);

    const fetchAvailableAcres = async (id, type) => {
        if (!id) return 0;

        try {
            if (isOregonSede && selectedCatalog === "sections" && type === "farm") {
                const [farmResponse, sectionsResponse] = await Promise.all([
                    get(`/fincas/farms/${id}`),
                    get("/fincas/sections"),
                ]);

                if (farmResponse.status === 200) {
                    const farmAcres = Number(
                        farmResponse?.data?.acres ??
                        farmResponse?.acres ??
                        0
                    ) || 0;

                    const farmSections = Array.isArray(sectionsResponse?.data)
                        ? sectionsResponse.data.filter((section) => {
                            const sectionFarmId =
                                section?.idfinca_farm ??
                                section?.farm?.idfinca_farm ??
                                section?.area?.idfinca_farm ??
                                section?.area?.farm?.idfinca_farm;
                            return String(sectionFarmId) === String(id);
                        })
                        : [];

                    const usedAcres = farmSections.reduce(
                        (sum, section) => sum + (Number(section?.acres) || 0),
                        0
                    );
                    return Math.max(0, farmAcres - usedAcres);
                }
            }

            let endpoint = "";

            if (type === "farm") {
                endpoint = `/fincas/farms/${id}`;
            } else if (type === "area") {
                endpoint = `/fincas/areas/${id}`;
            }

            const response = await get(endpoint);
            if (response.status === 200) {
                return response.availableAcres;
            }
        } catch (error) {
            console.error("Error fetching available acres:", error);
        }
        return 0;
    };

    const fetchData = async () => {
        if (!currentConfig || !currentConfig.endpoint) return;

        showLoader(true);
        try {
            const response = await get(currentConfig.endpoint);

            if (response.status === 200 && Array.isArray(response.data)) {
                if (isMounted.current) {
                    setData(response.data.map((item, index) => ({
                        id:
                            item.id ||
                            item[currentConfig.idKey] ||
                            item.idfinca_section ||
                            item.idfinca_area ||
                            item.idfinca_farm ||
                            `${selectedCatalog}-${index}`,
                        ...item,
                    })));
                }
            } else {
                showToast("No data found or invalid response.", "error");
            }
        } catch (error) {
            console.error("Error fetching data:", error);
            showToast("Error fetching data", "error");
        } finally {
            showLoader(false);
        }
    };

    const handleRowSelection = (selection) => {
        setSelectedRows([...selection]);
    };

    const handleRowClick = ({ row }) => {
        setSelectedRows((prevSelected) => {
            const alreadySelected = prevSelected.includes(row.id);
            const updatedSelection = alreadySelected
                ? prevSelected.filter((id) => id !== row.id)
                : [...prevSelected, row.id];
            return updatedSelection;
        });

        setSelectedRow(row);

        // Normalize many-to-many relationships
        const transformedData = { ...row };

        if (Array.isArray(row.fincasApplicationModes)) {
            transformedData.idfinca_application_modes = row.fincasApplicationModes.map(
                (mode) => mode.idfinca_application_mode
            );
        }

        if (Array.isArray(row.fincasProductSedes) && row.fincasProductSedes.length > 0) {
            const idsede = JSON.parse(localStorage.getItem("@sede"))?.id;

            const sedeData = row.fincasProductSedes.find(s => s.idsede === parseInt(idsede));
            if (sedeData) {
                transformedData.cost_per_presentation = sedeData.cost_per_presentation ?? 0;
                transformedData.total_presentation = sedeData.total_presentation ?? "";
            }
        }

        setFormData(transformedData);
    };

    const handleCloseDialog = () => {
        setConfirmDialogOpen(false);
        setPendingDeleteIds([]);
        setSelectedRows([]);
    };

    const normalizeSectionPayload = (payload) => {
        if (selectedCatalog !== "sections") {
            return payload;
        }

        payload.idfinca_area = payload.idfinca_area === "" ? null : (payload.idfinca_area ?? null);

        if (isOregonSede) {
            payload.idfinca_area = null;
        }

        return payload;
    };

    const store = async () => {
        try {
                const sedeId = JSON.parse(localStorage.getItem("@sede"))?.id;
                const payload = {
                   ...formData,
                    id_sede: sedeId,       
                };
                if (selectedCatalog === "products" && !isFloridaSede) {
                    delete payload.EPA;
                    delete payload.ingredients;
                }
                normalizeSectionPayload(payload);

                const response = await post(`${currentConfig.endpoint}/item/store`, payload);
                if (response.status === 200 || response.status === 201) {
                showToast(response.message, "success");
                fetchData();
                if (isMounted.current) {
                    setFormData({});
                }
                return true;
            }

            const errors = response.errors || [];
            if (errors.length === 1) {
                showToast(errors[0], "error");
                return false;
            }

            if (errors.length > 1) {
                showToast("Please complete all required fields correctly.", "error");
                return false;
            }

            throw new Error(response.message || "Unexpected response from the server.");
        } catch (error) {
            showToast(error.message || "An unexpected error occurred.", "error");
            return false;
        }
    };

    const update = async () => {
        try {
            if (!selectedRow) {
                showToast("No row selected. Please select a row to update.", "error");
                return;
            }
            const payload = { ...formData };
            if (selectedCatalog === "products" && !isFloridaSede) {
                delete payload.EPA;
                delete payload.ingredients;
            }
            normalizeSectionPayload(payload);
            const response = await put(`${currentConfig.endpoint}/item/${selectedRow.id}`, payload);
            if (response.status === 200) {
                showToast(response.message, "success");
                fetchData();
                if (isMounted.current) {
                    setFormData({});
                }
                return;
            }

            const errors = response.errors || [];
            if (errors.length === 1) {
                showToast(errors[0], "error");
                return;
            }

            if (errors.length > 1) {
                showToast("Please complete all required fields correctly.", "error");
                return;
            }

            throw new Error(response.message || "Unexpected response from the server.");
        } catch (error) {
            showToast(error.message || "An unexpected error occurred.", "error");
        }
    };

    const destroy = () => {
        if (!selectedRow && selectedRows.length === 0) {
            showToast("No row selected. Please select a row to delete.", "error");
            return;
        }

        const idsToDelete = selectedRows.length > 0 ? [...selectedRows] : [selectedRow.id];
        setPendingDeleteIds(idsToDelete);

        if (idsToDelete.length > 1) {
            setConfirmDialogOpen(true);
        } else {
            executeDelete(idsToDelete);
        }
    };


    const executeDelete = async (ids) => {
        try {
            const isMultiDelete = currentConfig.multiDelete || ids.length > 1;
            const endpoint = isMultiDelete
                ? `${currentConfig.endpoint}/delete/multiple`
                : `${currentConfig.endpoint}/item/${ids[0]}`;

            const idsede = localStorage.getItem("@sede")?.id || null;
            const payload = isMultiDelete ? { ids, idsede } : { idsede };
            const response = await del(endpoint, payload);

            if (response.status !== 200) {
                throw new Error(response.message || "Error deleting item(s). Please try again.");
            }

            showToast(response.message, "success");
            fetchData();
            trashRef.current?.refresh?.();
            setAcresRefreshKey((previousRefreshKey) => previousRefreshKey + 1);
            if (isMounted.current) {
                setSelectedRows([]);
                setPendingDeleteIds([]);
                setFormData({});
            }

        } catch (error) {
            showToast(error.message || "An unexpected error occurred.", "error");
        } finally {
            handleCloseDialog();
        }
    };

    const getDeleteMessage = () => {
        if (selectedCatalog === "sections") {
            return "You are about to delete multiple sections. This action cannot be undone. Do you want to proceed?";
        }
        if (selectedCatalog === "areas") {
            return "You are about to delete multiple areas. This action cannot be undone. Do you want to proceed?";
        }
        return "You are about to delete multiple items. This action cannot be undone. Do you want to proceed?";
    };

    const relationMap = {
        idfinca_measure: "fincasMeasure.unit",
        idfinca_application_modes: "fincasApplicationModes[].name",
        idfinca_product_type: "fincasProductType.name",
        idfinca_area: "area.name",
        idfinca_cultivation_type: "cultivationType.name",
        cost_per_presentation: "fincasProductSedes[].cost_per_presentation",
        total_presentation: "fincasProductSedes[].total_presentation",
        ...(selectedCatalog === "sections" ? { idfinca_farm: "area.farm.name" } : { idfinca_farm: "farm.name" })
    };

    const getRelatedValue = (params, fieldName) => {
        if (selectedCatalog === "sections" && fieldName === "idfinca_farm") {
            const farmId =
                params.row?.idfinca_farm ??
                params.row?.farm?.idfinca_farm ??
                params.row?.area?.idfinca_farm ??
                params.row?.area?.farm?.idfinca_farm;

            return (
                params.row?.farm?.name || params.row?.area?.farm?.name || farmsById[String(farmId)]
            );
        }

        if (selectedCatalog === "sections" && fieldName === "idfinca_cultivation_type") {
            return (
                params.row?.cultivationType?.name ||
                params.row?.fincasCultivationType?.name ||
                params.row?.fincaCultivationType?.name ||
                ""
            );
        }

        const path = relationMap[fieldName];
        if (!path) return params.row[fieldName];

        if (path.includes("[]")) {
            const [arrayPath, field] = path.split("[]");
            const items = arrayPath.split(".").reduce((acc, key) => acc && acc[key], params.row);
            if (Array.isArray(items)) {
                const labelField = field.replace(".", "");
                return items
                    .slice()
                    .sort((a, b) => (a.idfinca_application_mode || a.id || 0) - (b.idfinca_application_mode || b.id || 0))
                    .map((item) => item?.[labelField] || "")
                    .join(", ");
            }
            return "";
        }

        return path.split(".").reduce((acc, key) => acc && acc[key], params.row);
    };

    const getColumns = (selectedCatalog) => {
        const config = catalogConfig[selectedCatalog];
        if (!config) return [];
        const tableName = config.tableName;

        const columns = [
            {
                field: "no",
                headerName: "No.",
                flex: 1,
                valueGetter: (params) => params.api.getRowIndex(params.id) + 1,
            },
            ...config.fields
                .filter(f => !(isOregonSede && selectedCatalog === "sections" && f.name === "idfinca_area"))
                .filter(
                    (f) =>
                        !(
                            selectedCatalog === "products" &&
                            !isFloridaSede &&
                            floridaOnlyProductFields.includes(f.name)
                        )
                )
                .map((field) => ({
                field: field.name,
                headerName: field.label,
                flex: 2,
                valueGetter: (params) => getRelatedValue(params, field.name),
                renderCell: (params) => {
                    if (params.value == null || params.value === "") {
                        return "N/A";
                    }
                    if (field.isPriceField) {
                        const value = parseFloat(params.value || 0);
                        return `$${value.toFixed(4)}`;
                    }
                    if (relationMap[field.name]?.includes("[]")) {
                        return (
                            <div style={{ whiteSpace: "normal", wordWrap: "break-word", lineHeight: "1.5" }}>
                                {params.value}
                            </div>
                        );
                    }
                    return params.value;
                },
            })),
            {
            field: "history",
            headerName: "Actions",
            flex: 0.5,
            minWidth: 120,
            width: 120,
            sortable: false,
            filterable: false,
            renderCell: (params) => (
                <Link to={`/history/${tableName}/${params.row.id}`} target="_blank">
                    <Tooltip title="Show history" placement="top">
                        <IconButton>
                            <Icon>history</Icon>
                        </IconButton>
                    </Tooltip>
                 </Link>
                ),
            },
    ];
        return columns;
    };     
    return (
        <div className={classes.root}>
            <ViewTitle title={`Catalog: ${catalogConfig[catalog]?.label}`} />
            <Divider className={classes.divider} />

            {isOregonSede && selectedCatalog === "areas" && (
                <div style={{ marginBottom: 12, color: "#d32f2f", fontWeight: 600 }}>
                    Areas catalog is disabled for Oregon sede.
                </div>
            )}

            {selectedCatalog && !(isOregonSede && selectedCatalog === "areas") && (
                <>
                    <DynamicForm
                        formData={formData}
                        handleChange={(e) => setFormData({ ...formData, [e.target.name]: e.target.value })}
                        config={formConfig}
                        fetchAvailableAcres={fetchAvailableAcres}
                        acresRefreshKey={acresRefreshKey}
                    />
                    <Crud
                        permissions={{ create_attr: true, update_attr: allowUpdate, delete_attr: true }}
                        create={{
                            create: () => {
                                setFormData({});
                                setIsCreating(true);
                            },
                            store: async () => {
                                const ok = await store();
                                if (ok) {setIsCreating(false);}
                                return ok;
                            }
                        }}
                        update={update}
                        destroy={destroy}
                        cancel={() => {
                            setFormData({});
                            setIsCreating(false);
                        }}
                    />
                    {selectedCatalog && (
                        <Trash
                            ref={trashRef}
                            key={`${trashTable}-${trashId}`}
                            table={trashTable}
                            id={trashId}
                            onRefresh={fetchData}
                        />
                    )}
                    <div className={classes.tableContainer}>
                        <DataGrid
                            rows={data}
                            columns={getColumns(selectedCatalog)}
                            pageSize={10}
                            autoHeight
                            checkboxSelection={currentConfig.multiDelete || false}
                            selectionModel={selectedRows}
                            onSelectionModelChange={handleRowSelection}
                            onRowClick={handleRowClick}
                        />
                    </div>
                </>
            )}
            <Dialog
                open={confirmDialogOpen}
                onClose={handleCloseDialog}
                fullWidth
                maxWidth="sm"
            >
                <DialogContent className={classes.confirmDialog}>
                    <DialogContentText>{getDeleteMessage()}</DialogContentText>
                </DialogContent>
                <DialogActions className={classes.confirmDialogActions}>
                    <Button onClick={() => setConfirmDialogOpen(false)}>Disagree</Button>
                    <Button
                        className={classes.deleteButton}
                        onClick={() => executeDelete(pendingDeleteIds)}
                    >
                        Delete
                    </Button>
                </DialogActions>
            </Dialog>
        </div>
    );
};

export default Catalogs;